import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Divider, MenuItem, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PATH_AUTH, PATH_DASHBOARD, PATH_STUDENTS } from '@/routes/paths';
import { IconButtonAnimate } from '@/components/animate';
import { CustomAvatar } from '@/components/custom-avatar';
import Label from '@/components/label';
import MenuPopover from '@/components/menu-popover';
import { useNotify } from '@/components/snackbar';
import { whatsappValidationEnabled } from '@/consts/auth';
import { useSessionContext } from '@/auth/useSessionContext';
import { getVerificationStatus } from '@/auth/registrationCompletion';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import useAccessibleAccounts from '@/hooks/useAccessibleAccounts';
import useLoggedUser from '@/hooks/useLoggedUser';
import { switchToOwnProfile } from '@/layouts/dashboard/nav/AccountSwitcher/switchAccountAndBusiness';
import {
  clearCollaboratorsCache,
  fetchInvitationsService,
  validateImpersonatedAccountAccess,
} from '@/services/team';
import { confirmUserBeforeTrigger } from '@/utils/confirmUserBeforeTrigger';
import {
  IMPERSONATION_REVOKED_MESSAGE,
  IMPERSONATION_REVOKED_TITLE,
} from '@/auth/impersonationSession';
import { getDashboardHomePath } from '@/utils/dashboard';
import { BACKGROUND_QUERY_META } from '@/utils/backgroundQuery';

const AVATAR_SIZE = 40;

export default function AccountPopover() {
  const { user, logout, updateAmbient, refreshUser } = useSessionContext();
  const { impersonatedUserId, startImpersonation, stopImpersonation } = useImpersonation();
  const isImpersonating = !!impersonatedUserId;
  // Com o seletor de conta e negócio no menu lateral (CBK-595), a troca de conta sai daqui.
  const { accounts, isAvailable: isAccountSwitcherAvailable } = useAccessibleAccounts();
  // Com o seletor de conta e negócio, o menu de perfil é de quem está logado, mesmo dentro de
  // conta compartilhada (CBK-680): o usuário da sessão ali é o titular.
  const isInSharedAccount = isImpersonating && isAccountSwitcherAvailable;
  const loggedUser = useLoggedUser({ enabled: isInSharedAccount });
  const menuUser = (isInSharedAccount && loggedUser) || user;

  const isStudentPath = user?.viewPageAs === 'student';

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notify = useNotify();

  const [openPopover, setOpenPopover] = useState(null);
  const [startingCollaboratorId, setStartingCollaboratorId] = useState(null);
  const dashboardEntryPath = getDashboardHomePath(user);

  const handleOpenPopover = (event) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const pendingInvitesPath = `${PATH_DASHBOARD.general.team}?tab=invites`;

  const handleStartImpersonation = async (collaborator) => {
    const ownerId = collaborator?.owner?.id;
    if (!ownerId) return;

    setStartingCollaboratorId(collaborator.id);

    try {
      const hasAccess = await validateImpersonatedAccountAccess({ ownerId });

      if (!hasAccess) {
        clearCollaboratorsCache();
        queryClient.invalidateQueries({ queryKey: ['colaborators-invitations'] });
        notify.warning(IMPERSONATION_REVOKED_TITLE, {
          autoHideDuration: 8000,
          subtitle: IMPERSONATION_REVOKED_MESSAGE,
        });
        return;
      }

      handleClosePopover();
      navigate(dashboardEntryPath);
      startImpersonation(ownerId);
    } catch (error) {
      notify.error('Não foi possível validar o acesso como convidado.', {
        autoHideDuration: 5000,
      });
    } finally {
      setStartingCollaboratorId(null);
    }
  };

  const handleClickItem = (path) => {
    handleClosePopover();
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClosePopover();
      localStorage.removeItem('next');
      navigate(PATH_AUTH.login, { replace: true });
    } catch (error) {
      console.error(error);
      notify.error('Unable to logout!');
    }
  };

  const { data: collaboratorsData } = useQuery({
    queryKey: ['colaborators-invitations'],
    queryFn: fetchInvitationsService,
    enabled: !isImpersonating && !isAccountSwitcherAvailable,
    retry: false,
    meta: BACKGROUND_QUERY_META,
  });

  const collaborators = (collaboratorsData?.results || []).filter((collaborator) =>
    ['active', 'pending'].includes(collaborator?.status)
  );

  const sortedCollaborators = [
    ...collaborators.filter((collaborator) => collaborator?.status === 'active'),
    ...collaborators.filter((collaborator) => collaborator?.status === 'pending'),
  ];

  const shouldShowCollaborators =
    !isImpersonating && !isAccountSwitcherAvailable && sortedCollaborators.length > 0;

  const MAX_VISIBLE_COLLABORATORS = 3;
  const COLLABORATOR_ITEM_HEIGHT = 72;
  const collaboratorsMaxHeight = MAX_VISIBLE_COLLABORATORS * COLLABORATOR_ITEM_HEIGHT;
  const isCollaboratorsScrollable = sortedCollaborators.length > MAX_VISIBLE_COLLABORATORS;

  const OPTIONS = [
    {
      label: 'Página Inicial',
      linkTo: !isStudentPath ? dashboardEntryPath : PATH_STUDENTS.courses,
    },
  ];

  if (!isImpersonating) {
    OPTIONS.push({
      label: 'Meu Perfil',
      linkTo: !isStudentPath ? PATH_DASHBOARD.user.profile : PATH_STUDENTS.profile,
    });
  } else if (isInSharedAccount && !isStudentPath) {
    // O perfil abre na própria conta, e não dentro da compartilhada (CBK-681, daily de 03/11).
    OPTIONS.push({
      label: 'Meu perfil (titular)',
      linkTo: PATH_DASHBOARD.user.profile,
      onClick: () => {
        handleClosePopover();
        switchToOwnProfile(accounts);
      },
    });
  }

  if (!isStudentPath) {
    if (!isImpersonating) {
      OPTIONS.push(
        {
          label: 'Planos e Taxas',
          linkTo: `${PATH_DASHBOARD.user.profile}?tab=fees`,
        },
        // Fora da impersonação, como a rota: a página Equipe recusa o membro da
        // equipe que opera a conta do dono (`ImpersonationGuard`).
        {
          label: 'Equipe',
          linkTo: PATH_DASHBOARD.general.team,
        }
      );
    }
  }

  if (!isImpersonating) {
    OPTIONS.push({
      label: isStudentPath ? 'Mudar para produtor' : 'Mudar para painel do aluno',
      linkTo: isStudentPath ? dashboardEntryPath : PATH_STUDENTS.courses,
      onClick: async (option) => {
        const me = queryClient.getQueryData(['me']);
        const currentUser = me || user;

        if (whatsappValidationEnabled) {
          if (isStudentPath && currentUser?.whatsappValidated === false) {
            try {
              const confirmed = await confirmUserBeforeTrigger({
                currentValue: currentUser,
                shouldTrigger: (confirmedUser) => confirmedUser?.whatsappValidated === false,
                refresh: () => refreshUser({ forceUpdate: true }),
              });
              const verificationStatus = getVerificationStatus(confirmed.value);

              if (verificationStatus.isVerificationPending) {
                queryClient.setQueryData(['verificationStatus'], verificationStatus);
                queryClient.setQueryData(['isOpenVerifyModal'], true);
              }
            } catch (error) {
              const verificationStatus = getVerificationStatus(currentUser);

              if (verificationStatus.isVerificationPending) {
                queryClient.setQueryData(['verificationStatus'], verificationStatus);
                queryClient.setQueryData(['isOpenVerifyModal'], true);
              }
            }
          }
        }

        await updateAmbient({
          viewPageAs: isStudentPath ? 'producer' : 'student',
        });

        handleClickItem(option.linkTo);
      },
    });
  }

  // Com o seletor de conta e negócio, a troca de conta é só por ele (4.4 RF-01, CBK-680).
  if (!isStudentPath && isImpersonating && !isAccountSwitcherAvailable) {
    OPTIONS.push({
      label: 'Retornar para Minha Conta',
      linkTo: dashboardEntryPath,
      onClick: () => {
        handleClickItem(dashboardEntryPath);
        stopImpersonation();
      },
    });
  }

  return (
    <>
      {/* O nome não pode vir do avatar: sem foto ele é só a inicial, e o botão seria anunciado por ela. */}
      <IconButtonAnimate
        onClick={handleOpenPopover}
        aria-label="Menu da conta"
        sx={{
          p: 0,
          ...(openPopover && {
            // Do tamanho do avatar, e não do botão: no celular o botão tem 44 px de alvo de toque
            // e a sombra de 100% viraria um anel escuro em volta da foto.
            '&:before': {
              zIndex: 1,
              content: "''",
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              inset: 0,
              margin: 'auto',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <CustomAvatar
          src={menuUser?.picture || '/logo/green-logo-white-background.png'}
          alt={menuUser?.first_name}
          name={menuUser?.first_name}
          sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
        />
      </IconButtonAnimate>

      <MenuPopover open={openPopover} onClose={handleClosePopover} sx={{ width: 320, p: 0 }}>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: 'background.neutral',
            }}
          >
            <CustomAvatar
              src={menuUser?.picture || '/logo/green-logo-white-background.png'}
              alt={menuUser?.first_name}
              name={menuUser?.first_name}
              sx={{ width: 48, height: 48 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {`${menuUser?.first_name} ${menuUser?.last_name}` || menuUser?.email || 'Usuario'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                {menuUser?.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {shouldShowCollaborators && (
          <>
            <Box
              sx={{
                px: 1,
                py: 1,
                maxHeight: isCollaboratorsScrollable ? collaboratorsMaxHeight : 'unset',
                overflowY: isCollaboratorsScrollable ? 'auto' : 'visible',
                pr: isCollaboratorsScrollable ? 0.5 : 1,
              }}
            >
              <Stack>
                {sortedCollaborators.map((collaborator) => {
                  const owner = collaborator.owner || {};
                  const ownerName = owner.username || owner.email || 'Usuario';
                  const ownerEmail = owner.email || '';
                  const ownerPicture = owner.picture || '';
                  const isActiveCollaborator = collaborator?.status === 'active';
                  const canImpersonate = isActiveCollaborator && !!collaborator?.owner?.id;
                  const isStartingCollaborator = startingCollaboratorId === collaborator.id;
                  const statusLabelMapper = {
                    active: { color: 'primary', label: 'Ativo' },
                    pending: { color: 'warning', label: 'Pendente' },
                  };
                  const statusConfig =
                    statusLabelMapper[collaborator?.status] || statusLabelMapper.pending;

                  return (
                    <MenuItem
                      key={collaborator.id}
                      onClick={
                        canImpersonate
                          ? () => handleStartImpersonation(collaborator)
                          : () => handleClickItem(pendingInvitesPath)
                      }
                      disabled={isStartingCollaborator}
                      sx={{
                        gap: 1.5,
                        alignItems: 'center',
                        height: COLLABORATOR_ITEM_HEIGHT,
                      }}
                    >
                      <CustomAvatar
                        src={ownerPicture}
                        alt={ownerName}
                        name={ownerName}
                        sx={{ width: 36, height: 36 }}
                      />
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                          {ownerName}
                        </Typography>
                        {ownerEmail && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                            {ownerEmail}
                          </Typography>
                        )}
                      </Box>
                      <Label
                        variant="soft"
                        color={statusConfig.color}
                        sx={{ height: 24, px: 1 }}
                      >
                        {statusConfig.label}
                      </Label>
                    </MenuItem>
                  );
                })}
              </Stack>
            </Box>

            <Divider sx={{ borderStyle: 'dashed' }} />
          </>
        )}

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() =>
                option.onClick ? option.onClick(option) : handleClickItem(option.linkTo)
              }
              sx={{
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                minHeight: 48,
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={async () => {
            await handleLogout();
          }}
          sx={{ m: 1 }}
        >
          Logout
        </MenuItem>
      </MenuPopover>
    </>
  );
}
